import { useState, useEffect, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface UserProfile {
  id: string;
  role: 'driver' | 'fleet_manager';
  full_name: string;
  phone: string | null;
  company_id: string | null;
  created_at: string;
}

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    loading: true,
  });

  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    if (error || !data) return null;
    return data as UserProfile;
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;

      if (session?.user) {
        const profile = await fetchProfile(session.user.id);
        if (mounted) {
          setState({ user: session.user, profile, loading: false });
        }
      } else {
        if (mounted) setState({ user: null, profile: null, loading: false });
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;
      if (session?.user) {
        const profile = await fetchProfile(session.user.id);
        if (mounted) setState({ user: session.user, profile, loading: false });
      } else {
        if (mounted) setState({ user: null, profile: null, loading: false });
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signUp = useCallback(async (
    email: string,
    password: string,
    fullName: string,
    role: 'driver' | 'fleet_manager'
  ): Promise<{ error: string | null }> => {
    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: { data: { display_name: fullName.trim(), role } },
    });

    if (error) {
      if (error.message.includes('already registered')) {
        return { error: 'Cet email est déjà utilisé. Connectez-vous à la place.' };
      }
      return { error: error.message };
    }

    // Create profile row
    if (data.user) {
      await supabase.from('profiles').upsert({
        id: data.user.id,
        role,
        full_name: fullName.trim(),
        phone: null,
        company_id: null,
      });
    }

    return { error: null };
  }, []);

  const signIn = useCallback(async (
    email: string,
    password: string
  ): Promise<{ error: string | null; role?: string }> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (error || !data.user) {
      return { error: 'Email ou mot de passe incorrect.' };
    }

    // Fetch profile to get role
    const profile = await fetchProfile(data.user.id);
    return { error: null, role: profile?.role ?? data.user.user_metadata?.role };
  }, [fetchProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut({ scope: 'local' });
    setState({ user: null, profile: null, loading: false });
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) return { error: error.message };
    return { error: null };
  }, []);

  return {
    user: state.user,
    profile: state.profile,
    loading: state.loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
  };
}
